import { Container, Graphics, Sprite, type Texture } from "pixi.js";
import type { GridPoint, PresentationTicker } from "@slot/presentation";
import type { CanvasPoint } from "@slot/game-shell";
import type { SecretBonusPort } from "./SecretBonusPresenter";

/** The hand on the Secret Bonus cell and its tap (design units in the grid's space; engine seconds). */
export interface SecretBonusHandTuning {
  /** The cell's side: a tap inside it is on the symbol. */
  cellSize: number;
  /** The hand's fingertip, from the cell's centre (the disc's lower right, as the layout example points it). */
  tipX: number;
  tipY: number;
  /** The hand's drawn height. */
  height: number;
  /** The fingertip's place in the hand art (0..1 of its width and height). */
  tipAnchorX: number;
  tipAnchorY: number;
  /** One tap: the hand presses `pressDistance` towards the disc and back, once every `periodSeconds`. */
  periodSeconds: number;
  pressDistance: number;
  /** The hand's scale at the bottom of the press (a finger pressing in). */
  pressScale: number;
}

/**
 * The hand that teaches the Secret Bonus, in the grid's space over the symbols: on the offered cell, its fingertip on
 * the disc, tapping — every time the symbol is on the board. The tap is drawn on the presentation ticker (engine time,
 * paused with the dev tools), not as a runner task: the offer lasts while the game is idle, and a task would keep it busy.
 * An invisible square over the cell, `SecretBonus`, is the cell the tap reads (and the place an e2e taps).
 */
export class SecretBonusView extends Container implements SecretBonusPort {
  private readonly cell: Graphics;
  private readonly hand: Sprite | null;
  private stopTicking: (() => void) | null = null;
  private phase = 0;
  private handRestScale = 1;

  public constructor(
    private readonly deps: {
      hand: Texture | null;
      /** A cell's centre in the grid's space (the grid layer's layout). */
      cellPosition: (positionIndex: number) => GridPoint;
      ticker: PresentationTicker;
      tuning: SecretBonusHandTuning;
    },
  ) {
    super({ label: "SecretBonusLayer" });
    const { cellSize } = deps.tuning;
    this.cell = new Graphics().rect(-cellSize / 2, -cellSize / 2, cellSize, cellSize).fill({ color: 0xffffff, alpha: 0 });
    this.cell.label = "SecretBonus";
    this.addChild(this.cell);
    const handArt = deps.hand;
    this.hand = handArt ? new Sprite({ texture: handArt, label: "SecretBonusHand" }) : null;
    if (this.hand && handArt) {
      this.hand.anchor.set(deps.tuning.tipAnchorX, deps.tuning.tipAnchorY);
      this.handRestScale = deps.tuning.height / handArt.height;
      this.addChild(this.hand);
    }
    this.visible = false;
  }

  public show(positionIndex: number): void {
    const centre = this.deps.cellPosition(positionIndex);
    this.cell.position.set(centre.x, centre.y);
    this.phase = 0;
    this.pose(0);
    this.visible = true;
    this.stopTicking?.();
    this.stopTicking = this.deps.ticker.add((deltaSeconds) => {
      this.phase = (this.phase + deltaSeconds / this.deps.tuning.periodSeconds) % 1;
      this.pose(this.phase);
    });
  }

  public hide(): void {
    this.visible = false;
    this.stopTicking?.();
    this.stopTicking = null;
  }

  public hits(globalPoint: CanvasPoint, positionIndex: number): boolean {
    const local = this.toLocal(globalPoint);
    const centre = this.deps.cellPosition(positionIndex);
    const half = this.deps.tuning.cellSize / 2;
    return Math.abs(local.x - centre.x) <= half && Math.abs(local.y - centre.y) <= half;
  }

  /** The hand at `phase` (0..1) of one tap: a quick press towards the disc in the first third, a slow return after. */
  private pose(phase: number): void {
    if (!this.hand) return;
    const t = this.deps.tuning;
    const press = phase < 1 / 3 ? Math.sin(phase * 3 * (Math.PI / 2)) : Math.cos(((phase - 1 / 3) * 1.5) * (Math.PI / 2));
    const offset = t.pressDistance * Math.max(0, press);
    // Towards the disc: up and to the left, along the finger.
    this.hand.position.set(this.cell.x + t.tipX - offset * 0.7, this.cell.y + t.tipY - offset * 0.7);
    const scale = this.handRestScale * (1 + (t.pressScale - 1) * Math.max(0, press));
    this.hand.scale.set(scale, scale);
  }
}
